import { EnumButtonName } from '@calm/evexia-analytics';
import { Scale } from '@rhg/hooks/use-screening-results-scales';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Grid, Theme, Typography, useTheme } from '@rhg/libs/material';
import { InfoGradient, InfoOutline } from '@rhg/libs/material/icons';
import SimpleDialog, {
    SimpleDialogProps,
} from '@rhg/libs/material/simple-dialog';
import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

export interface ScaleDialogProps
    extends Pick<SimpleDialogProps, 'isOpen' | 'onClose' | 'trackDialog'> {
    title: string;
    scales: Scale[];
    trackCloseButton: EnumButtonName;
}

const styleIconWrapper = {
    height: 51,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'dark' ? 'info.dark' : 'info.light',
    padding: 2,
    borderRadius: '50%',
};
const styleTitle = { mb: 0, pt: 4 };
const styleContentWrapper = { pb: 0, mb: 4, maxWidth: 'none' };
const styleBottomActionsWrapper = { pt: 0, pb: 4 };

const styleScaleIndicator = (color: string) => ({
    backgroundColor: color,
    width: 13,
    height: 13,
    borderRadius: '50%',
    border: '1px solid',
    borderColor: 'text.disabled',
});

const styleScale = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    py: 1,
};

const styleCloseButton = {
    maxWidth: 140,
    mx: 'auto',
};

const styleInfoIconDarkMode = { width: 36, height: 36 };

export const ScaleDialog = memo(function ScaleDialog({
    isOpen,
    onClose,
    title,
    trackCloseButton,
    trackDialog,
    scales,
}: ScaleDialogProps) {
    const { formatMessage } = useIntl();
    const {
        palette: { mode },
    } = useTheme();

    const icon = useMemo(
        () => (
            <Box sx={styleIconWrapper}>
                {mode === 'dark' ? (
                    <InfoOutline color="info" style={styleInfoIconDarkMode} />
                ) : (
                    <InfoGradient />
                )}
            </Box>
        ),
        [mode]
    );

    return (
        <SimpleDialog
            trackDialog={trackDialog}
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            titleVariant="h6"
            titleColor="text.body"
            titleStyle={styleTitle}
            icon={icon}
            dialogMaxWidth={900}
            fullWidth
            inheritIconSize
            disableMobileFullscreen
            content={
                <Grid
                    container
                    gap={2}
                    direction="column"
                    justifyContent="center"
                >
                    {scales.map(({ label, color }) => (
                        <Grid sx={styleScale} key={label} item>
                            <Box sx={styleScaleIndicator(color)} />
                            <Typography variant="body1" color="text.body">
                                {label}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            }
            contentWrapperStyle={styleContentWrapper}
            bottomActionsWrapperStyle={styleBottomActionsWrapper}
            buttons={[
                {
                    trackButton: trackCloseButton,
                    title: formatMessage(commonMessages['common.ok']),
                    onClick: onClose,
                    size: 'small',
                    sx: styleCloseButton,
                },
            ]}
        />
    );
});
